import {
  iMessage,
  iMessageAttachment,
} from "@sdk/conversations/conversations.models";
import { useSDKActionWithDeps } from "@sdk/sdk.hooks";
import {
  extractMessengerSdpAnswer,
  iFBCallLog,
  normalizeFBCallDirection,
} from "@sdk/fb-call-logs/fb-call-logs-model";
import { Card, Typography, message } from "antd";
import { LoadingIndicatorWithoutSpin } from "components/common/loading-indicator/loading-indicator";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { loadFBCallLogById } from "store/modules/fb-call-logs/fb-call-logs.helpers";
import { selectFBCallLogById } from "store/modules/fb-call-logs/fb-call-logs.selectors";
import { selectCurrentUserId } from "store/modules/users/users.selectors";
import { useQueryWithStore } from "store/store.hooks";
import { isFBCallLogActive } from "./call-status";
import { AudioDeviceSelector } from "./components/audio-device-selector";
import { IncomingFBCallContactDetails } from "./components/incoming-fb-call-contact-details";
import { IncomingFBCallControls } from "./components/incoming-fb-call-controls";
import { IncomingFBCallStatus } from "./components/incoming-fb-call-status";
import { OutgoingFBCallControls } from "./components/outgoing-fb-call-controls";
import { OutgoingFBCallStatus } from "./components/outgoing-fb-call-status";
import { useWebRTCIncomingFBCall } from "./hooks/use-web-rtc-incoming-fb-call";
import { useWebRTCOutgoingFBCall } from "./hooks/use-web-rtc-outgoing-fb-call";

const { Text, Title } = Typography;

export const FacebookCallAttachment = ({
  attachment,
  message,
}: {
  attachment: iMessageAttachment;
  message: iMessage;
}) => {
  const callLogId = attachment.payload.callLogId as string;

  if (attachment?.payload?.outgoingCall) {
    return (
      <FacebookOutgoingCallCard
        callLogId={callLogId}
        browserId={attachment.payload.browserId as string}
        messageId={message.id!}
        conversationId={message.conversationId}
      />
    );
  }

  if (!callLogId) {
    return null;
  }

  return <FacebookIncomingCallCard callLogId={callLogId} />;
};

export const FacebookIncomingCallCard = ({
  callLogId,
}: {
  callLogId: string;
}) => {
  const { state: waCallLog, isLoading: isLoading } = useQueryWithStore(
    selectFBCallLogById(callLogId),
    loadFBCallLogById(callLogId)(),
    [callLogId],
    !callLogId,
  );

  if (isLoading) {
    return <LoadingIndicatorWithoutSpin />;
  }

  if (!waCallLog) {
    return null;
  }

  if (isFBCallLogActive(waCallLog)) {
    return <FacebookIncomingCallContainer callLog={waCallLog} />;
  }

  return (
    <Card size="small" className="mb-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <i className="ri-phone-line text-red-500"></i>
          <Text type="secondary">Call ended</Text>
        </div>
        <Text type="secondary" className="text-xs">
          {waCallLog.duration
            ? `${Math.floor(waCallLog.duration / 60)}:${(
                waCallLog.duration % 60
              )
                .toString()
                .padStart(2, "0")}`
            : ""}
        </Text>
      </div>
    </Card>
  );
};

export const FacebookIncomingCallContainer = ({
  callLog,
}: {
  callLog: iFBCallLog;
}) => {
  const data = callLog.callData!;
  const userId = useSelector(selectCurrentUserId);
  const recordingConfig = useMemo(
    () =>
      callLog.id && userId
        ? {
            organizationId: callLog.organizationId,
            conversationId: callLog.conversationId,
            callId: callLog.id,
            userId,
          }
        : null,
    [callLog.id, callLog.organizationId, callLog.conversationId, userId],
  );

  const [callState, setCallState] = useState<
    "incoming" | "connecting" | "connected" | "ended"
  >("incoming");
  const [connectionId, setConnectionId] = useState<string>("");
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const {
    peerConnection,
    localStream,
    remoteStream,
    isMuted,
    isSpeakerOn,
    acceptCall,
    applyRemoteAnswer,
    rejectCall,
    terminateCall,
    toggleMute,
    toggleSpeaker,
    setAudioInputDevice,
    setAudioOutputDevice,
    audioInputDevices,
    audioOutputDevices,
    selectedAudioInput,
    selectedAudioOutput,
    callDuration,
    isProcessing,
  } = useWebRTCIncomingFBCall(data, recordingConfig);

  // Effect to manage audio element
  useEffect(() => {
    if (audioRef.current && remoteStream) {
      audioRef.current.srcObject = remoteStream;
      // Set initial mute state based on speaker state
      audioRef.current.muted = !isSpeakerOn;
      if (isSpeakerOn) {
        audioRef.current.volume = 1.0;
      }
    }
  }, [remoteStream, isSpeakerOn]);

  // Effect to manage speaker state
  useEffect(() => {
    if (audioRef.current) {
      if (isSpeakerOn) {
        audioRef.current.muted = false;
        audioRef.current.volume = 1.0;
      } else {
        audioRef.current.muted = true;
      }
    }
  }, [isSpeakerOn]);

  const { doAction: acceptFBCall, isProcessing: isAcceptingCall } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => async () => {
          setCallState("connecting");
          // Messenger C2B: business sends SDP offer; API returns SDP answer
          const sdpOffer = await acceptCall();
          const result = await SDK.FBCallLogs.acceptCall({
            callLogId: callLog.id!,
            sdp: sdpOffer,
          });
          const answerSdp = extractMessengerSdpAnswer(result?.session);
          if (answerSdp) {
            await applyRemoteAnswer(answerSdp);
          }
          return result;
        },
        failureMessage: "Failed to accept call",
        successMessage: "Call accepted",
      }),
      [acceptCall, applyRemoteAnswer, callLog.id],
    );

  const { doAction: rejectFBCall, isProcessing: isRejectingCall } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => async () => {
          setCallState("ended");
          await rejectCall();
          return SDK.FBCallLogs.rejectCall({
            callLogId: callLog.id!,
          });
        },
        failureMessage: "Failed to reject call",
        successMessage: "Call rejected",
      }),
      [rejectCall, callLog.id],
    );

  const { doAction: terminateFBCall, isProcessing: isTerminatingCall } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => async () => {
          setCallState("ended");
          await terminateCall();
          return SDK.FBCallLogs.terminateCall({
            callLogId: callLog.id!,
          });
        },
        failureMessage: "Failed to terminate call",
        successMessage: "Call terminated",
      }),
      [terminateCall, callLog.id],
    );

  const handleAcceptCall = async () => {
    try {
      await acceptFBCall();
      setCallState("connected");
    } catch (error) {
      setCallState("incoming");
      message.error("Failed to accept call");
    }
  };

  const handleRejectCall = async () => {
    try {
      await rejectFBCall();
    } catch (error) {
      message.error("Failed to reject call");
    }
  };

  const handleTerminateCall = async () => {
    try {
      await terminateFBCall();
    } catch (error) {
      message.error("Failed to terminate call");
    }
  };

  return (
    <Card size="small" className="mb-2">
      <div className="space-y-3">
        {/* Call Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <i
              className={`ri-phone-line ${
                callState === "connected"
                  ? "text-green-500"
                  : callState === "connecting"
                  ? "text-yellow-500"
                  : "text-red-500"
              }`}
            ></i>
            <Title level={5} className="mb-0">
              Messenger Call
            </Title>
          </div>
          <IncomingFBCallStatus
            state={callState}
            duration={callDuration}
            direction={normalizeFBCallDirection(data)}
          />
        </div>

        {/* Contact Info */}
        <IncomingFBCallContactDetails
          contactId={callLog.contactId}
          connectionId={callLog.connectionId}
          callData={data}
          psid={callLog.psid}
        />

        {/* Audio Device Selection - Only show when call is connected */}
        {callState === "connected" && (
          <div className="space-y-2">
            <AudioDeviceSelector
              audioInputDevices={audioInputDevices}
              audioOutputDevices={audioOutputDevices}
              selectedAudioInput={selectedAudioInput}
              selectedAudioOutput={selectedAudioOutput}
              onAudioInputChange={setAudioInputDevice}
              onAudioOutputChange={setAudioOutputDevice}
            />
          </div>
        )}

        {/* Call Controls */}
        <IncomingFBCallControls
          callState={callState}
          isMuted={isMuted}
          isSpeakerOn={isSpeakerOn}
          isProcessing={
            isProcessing ||
            isAcceptingCall ||
            isRejectingCall ||
            isTerminatingCall
          }
          onAccept={handleAcceptCall}
          onReject={handleRejectCall}
          onTerminate={handleTerminateCall}
          onToggleMute={toggleMute}
          onToggleSpeaker={toggleSpeaker}
        />

        {/* Audio Elements */}
        {remoteStream && (
          <audio ref={audioRef} autoPlay style={{ display: "none" }} />
        )}
      </div>
    </Card>
  );
};

export const FacebookOutgoingCallCard = ({
  conversationId,
  messageId,
  callLogId,
  browserId,
}: {
  conversationId: string;
  /** Optional when rendering from FloatingFacebookCallContainer (answer-only flow). */
  messageId?: string;
  callLogId?: string;
  browserId: string;
}) => {
  const { state: waCallLog, isLoading: isLoading } = useQueryWithStore(
    selectFBCallLogById(callLogId || ""),
    loadFBCallLogById(callLogId || "")(),
    [callLogId],
    !callLogId,
  );

  // If call is completed or rejected, show ended state
  // Only show this if we have a callLog (not during initial loading)
  if (waCallLog && !isFBCallLogActive(waCallLog)) {
    return (
      <Card size="small" className="mb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <i className="ri-phone-line text-red-500"></i>
            <Text type="secondary">Call ended</Text>
          </div>
          <Text type="secondary" className="text-xs">
            {waCallLog.duration
              ? `${Math.floor(waCallLog.duration / 60)}:${(
                  waCallLog.duration % 60
                )
                  .toString()
                  .padStart(2, "0")}`
              : ""}
          </Text>
        </div>
      </Card>
    );
  }

  // Always render the container to prevent unmounting/remounting
  // This ensures pcRef persists across callLogId transitions
  return (
    <FacebookOutgoingCallContainer
      callLog={waCallLog}
      conversationId={conversationId}
      messageId={messageId}
      browserId={browserId}
    />
  );
};

export const FacebookOutgoingCallContainer = ({
  callLog,
  conversationId,
  messageId,
  browserId,
}: {
  callLog?: iFBCallLog;
  conversationId: string;
  /** Optional when rendering from FloatingFacebookCallContainer (answer-only flow). Initiate is disabled when undefined. */
  messageId?: string;
  browserId: string;
}) => {
  const data = callLog?.callData;
  const userId = useSelector(selectCurrentUserId);
  const recordingConfig = useMemo(
    () =>
      callLog?.id && userId
        ? {
            organizationId: callLog.organizationId,
            conversationId: callLog.conversationId ?? conversationId,
            callId: callLog.id,
            userId,
          }
        : null,
    [
      callLog?.id,
      callLog?.organizationId,
      callLog?.conversationId,
      conversationId,
      userId,
    ],
  );

  const [callState, setCallState] = useState<
    "outgoing_init" | "connecting" | "connected" | "ended"
  >(
    (() => {
      // If no callLog, we're in init state
      if (!callLog) {
        return "outgoing_init";
      }
      // Initialize state based on call log status
      if (callLog.callStatus === "OUTGOING_INIT") {
        return "outgoing_init";
      } else if (
        callLog.callStatus === "OUTGOING_INIT_SUCCESS" ||
        callLog.callStatus === "RINGING"
      ) {
        return "connecting";
      } else if (callLog.callStatus === "IN_PROGRESS") {
        return "connected";
      } else {
        return "ended";
      }
    })(),
  );

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const {
    peerConnection,
    localStream,
    remoteStream,
    isMuted,
    isSpeakerOn,
    initiateCall,
    applyRemoteAnswer,
    rejectCall,
    terminateCall,
    toggleMute,
    toggleSpeaker,
    setAudioInputDevice,
    setAudioOutputDevice,
    audioInputDevices,
    audioOutputDevices,
    selectedAudioInput,
    selectedAudioOutput,
    callDuration,
    isProcessing,
  } = useWebRTCOutgoingFBCall(data, recordingConfig);

  // Update call state based on call log status changes
  useEffect(() => {
    if (!callLog) {
      return;
    }
    if (callLog.callStatus === "OUTGOING_INIT") {
      setCallState("outgoing_init");
    } else if (
      callLog.callStatus === "OUTGOING_INIT_SUCCESS" ||
      callLog.callStatus === "RINGING"
    ) {
      setCallState("connecting");
    } else if (callLog.callStatus === "IN_PROGRESS") {
      setCallState("connected");
    } else if (
      callLog.callStatus === "COMPLETED" ||
      callLog.callStatus === "REJECTED"
    ) {
      setCallState("ended");
    }
  }, [callLog]);

  // Effect to manage audio element
  useEffect(() => {
    if (audioRef.current && remoteStream) {
      audioRef.current.srcObject = remoteStream;
      // Set initial mute state based on speaker state
      audioRef.current.muted = !isSpeakerOn;
      if (isSpeakerOn) {
        audioRef.current.volume = 1.0;
      }
    }
  }, [remoteStream, isSpeakerOn]);

  // Effect to manage speaker state
  useEffect(() => {
    if (audioRef.current) {
      if (isSpeakerOn) {
        audioRef.current.muted = false;
        audioRef.current.volume = 1.0;
      } else {
        audioRef.current.muted = true;
      }
    }
  }, [isSpeakerOn]);

  const { doAction: initiateFBCall, isProcessing: isInitiatingCall } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => async () => {
          if (messageId == null) return;
          setCallState("connecting");
          const sdpOffer = await initiateCall();
          const result = await SDK.FBCallLogs.initiateCall({
            conversationId,
            messageId,
            sdpOffer,
          });
          const answerSdp = extractMessengerSdpAnswer(result?.session);
          if (answerSdp) {
            await applyRemoteAnswer(answerSdp);
          }
          return result;
        },
        failureMessage: "Failed to initiate call",
        successMessage: "Call initiated",
        // Reset UI if backend rejects (e.g. missing identity) — identity-aware
        // copy comes from processServerError.
        onFailed: () => setCallState("outgoing_init"),
      }),
      [conversationId, initiateCall, applyRemoteAnswer, messageId],
    );

  const { doAction: terminateFBCall, isProcessing: isTerminatingCall } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => async () => {
          setCallState("ended");
          await terminateCall();
          if (callLog?.id) {
            return SDK.FBCallLogs.terminateCall({
              callLogId: callLog.id,
            });
          }
        },
        failureMessage: "Failed to terminate call",
        successMessage: "Call terminated",
      }),
      [terminateCall, callLog?.id],
    );

  const handleInitiateCall = useCallback(async () => {
    try {
      await initiateFBCall();
    } catch (error) {
      setCallState("outgoing_init");
      message.error("Failed to initiate call");
    }
  }, [initiateFBCall]);

  const handleCancelCall = useCallback(async () => {
    try {
      setCallState("ended");
      await rejectCall();
      if (callLog?.id) {
        await terminateFBCall();
      }
    } catch (error) {
      message.error("Failed to cancel call");
    }
  }, [rejectCall, terminateFBCall, callLog?.id]);

  const handleTerminateCall = useCallback(async () => {
    try {
      await terminateFBCall();
    } catch (error) {
      message.error("Failed to terminate call");
    }
  }, [terminateFBCall]);

  // Auto-initiate call if needed (when callLogId is undefined, browserId matches, and we have messageId)
  useEffect(() => {
    console.log(
      "messageId",
      messageId,
      callState,
      !callLog,
      browserId === (window as any).browserSessionId,
    );
    if (
      messageId != null &&
      callState === "outgoing_init" &&
      !callLog &&
      browserId === (window as any).browserSessionId
    ) {
      console.log("initiating call");
      handleInitiateCall();
    }
  }, [messageId, callState, callLog, browserId, handleInitiateCall]);

  // Show loading indicator during auto-init (when callLog is undefined)
  if (!callLog && callState === "outgoing_init") {
    return <LoadingIndicatorWithoutSpin />;
  }

  return (
    <Card size="small" className="mb-2">
      <div className="space-y-3">
        {/* Call Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <i
              className={`ri-phone-line ${
                callState === "connected"
                  ? "text-green-500"
                  : callState === "connecting"
                  ? "text-yellow-500"
                  : callState === "ended"
                  ? "text-red-500"
                  : "text-orange-500"
              }`}
            ></i>
            <Title level={5} className="mb-0">
              Messenger Call
            </Title>
          </div>
          <OutgoingFBCallStatus
            state={callState}
            duration={callDuration}
            direction={normalizeFBCallDirection(data)}
          />
        </div>

        {/* Contact Info */}
        {callLog && (
          <IncomingFBCallContactDetails
            contactId={callLog.contactId}
            connectionId={callLog.connectionId}
            callData={data}
            psid={callLog.psid}
          />
        )}

        {/* Audio Device Selection - Only show when call is connected */}
        {callState === "connected" && (
          <div className="space-y-2">
            <AudioDeviceSelector
              audioInputDevices={audioInputDevices}
              audioOutputDevices={audioOutputDevices}
              selectedAudioInput={selectedAudioInput}
              selectedAudioOutput={selectedAudioOutput}
              onAudioInputChange={setAudioInputDevice}
              onAudioOutputChange={setAudioOutputDevice}
            />
          </div>
        )}

        {/* Call Controls - Don't show init controls during auto-init. Initiate only when messageId is set (e.g. from message list, not floating). */}
        {callLog && (
          <OutgoingFBCallControls
            callState={callState}
            isMuted={isMuted}
            isSpeakerOn={isSpeakerOn}
            isProcessing={isProcessing || isInitiatingCall || isTerminatingCall}
            onInitiate={messageId != null ? handleInitiateCall : undefined}
            onCancel={handleCancelCall}
            onTerminate={handleTerminateCall}
            onToggleMute={toggleMute}
            onToggleSpeaker={toggleSpeaker}
          />
        )}

        {/* Audio Elements */}
        {remoteStream && (
          <audio ref={audioRef} autoPlay style={{ display: "none" }} />
        )}
      </div>
    </Card>
  );
};
